import { Subject, Observable, merge } from 'rxjs';
import { map, takeUntil, filter, scan } from 'rxjs/operators';

import { Inject, Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { SnAbilityService } from '@lib/ability';
import { SnShare } from '@lib/api/entities';
import { SnCurrentOrganization } from '@lib/auth/services/current-organization.service';
import { SnCollection } from '@lib/common/models';
import { t } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SN_DRAWER_DATA, SnDrawerService } from '@lib/ui/drawer';

import { FUSUS_CROSS_REGION_DEV } from '../../constants';
import { ISnCanShare, ISnIsSharable } from '../../interfaces';
import { IShareConfigDrawerDataV2, ShareConfigDrawerV2Container } from '../share-config-drawer-v2/share-config-drawer-v2.container';
import {
  IShareConfigDrawerData,
  ShareConfigDrawerContainer,
} from '../share-config-drawer/share-config-drawer.container';
import {
  IShareInviteDrawerV2Data,
  ShareInviteDrawerV2Container,
} from '../share-invite-drawer-v2/share-invite-drawer-v2.container';
import {
  IShareInviteDrawerData,
  ShareInviteDrawerContainer,
} from '../share-invite-drawer/share-invite-drawer.container';

export interface ISnShareDrawerData {
  object: ISnIsSharable;
  handlers: ISnCanShare;
}

@Component({
  selector: 'sn-share-drawer',
  templateUrl: './share-drawer.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./share-drawer.container.scss'],
})
export class ShareDrawerContainer implements OnDestroy {
  public readonly canAdd: boolean;
  public readonly canChange: boolean;
  public readonly canDelete: boolean;

  public readonly shares$: Observable<SnCollection<SnShare>>;
  public readonly shareActions: SnActionConfig[];

  private readonly _add$ = new Subject<SnShare[]>();
  private readonly _remove$ = new Subject<SnShare[]>();
  private readonly _update$ = new Subject<SnShare[]>();

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _drawer: SnDrawerService,
    private readonly _currentOrganization: SnCurrentOrganization,
    @Inject(SN_DRAWER_DATA) public readonly data: ISnShareDrawerData,
    ability: SnAbilityService,
  ) {
    this.canAdd = ability.can({ subject: 'sharing/share', action: 'add' });
    this.canChange = ability.can({ subject: 'sharing/share', action: 'change' });
    this.canDelete = ability.can({ subject: 'sharing/share', action: 'delete' });

    this.shareActions = this._getShareActions();
    this.shares$ = this._connectShares();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public addShare(): void {
    const isFususCrossRegionDevEnabled = this._currentOrganization.settings[FUSUS_CROSS_REGION_DEV];
    const drawerData = {
      object: this.data.object,
      createHandler: this.data.handlers.createShare.bind(this.data.handlers),
    };

    let drawer;
    if (isFususCrossRegionDevEnabled) {
      drawer = this._drawer.open<ShareInviteDrawerV2Container, IShareInviteDrawerV2Data>(
        ShareInviteDrawerV2Container,
        {
          disableClose: true,
          data: drawerData as IShareInviteDrawerV2Data,
        }
      );
    } else {
      drawer = this._drawer.open<ShareInviteDrawerContainer, IShareInviteDrawerData>(
        ShareInviteDrawerContainer,
        {
          disableClose: true,
          data: drawerData,
        }
      );
    }

    drawer.afterClosed$.pipe(
      filter((shares) => !!shares),
      takeUntil(this._destroy$),
    ).subscribe((shares) => {
      this._add$.next(shares);
    });
  }

  public deleteShare(share: SnShare): void {
    this.data.handlers
      .deleteShare(this.data.object.id, share.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._remove$.next([share]);
      });
  }

  public configureShare(targetShare: SnShare): void {
    const isFususCrossRegionDevEnabled = this._currentOrganization.settings[FUSUS_CROSS_REGION_DEV];
    const drawerConfig = {
      backdropClass: 'transparent',
      data: {
        share: targetShare,
        updateHandler: this.data.handlers.updateShare.bind(this.data.handlers),
      },
    }
    let drawer;
    
    if (isFususCrossRegionDevEnabled) {
      drawer = this._drawer.open<ShareConfigDrawerV2Container, IShareConfigDrawerDataV2>(
        ShareConfigDrawerV2Container,
        drawerConfig
      );
    } else {
      drawer = this._drawer.open<ShareConfigDrawerContainer, IShareConfigDrawerData>(
        ShareConfigDrawerContainer,
        drawerConfig,
      );
    }

    drawer.afterClosed$
      .pipe(
        filter((share) => !!share),
        takeUntil(this._destroy$),
      )
      .subscribe((share) => {
        this._update$.next([share]);
      });
  }

  private _connectShares(): Observable<SnCollection<SnShare>> {
    const feed$ = this.data.handlers
      .getShareList(this.data.object.id, { pageSize: 'all' })
      .pipe(map((response) => response.results));

    const add$ = merge(this._add$, feed$).pipe(map((shares) => ({ shares, action: 'add' })));
    const update$ = this._update$.pipe(map((shares) => ({ shares, action: 'update' })));
    const delete$ = this._remove$.pipe(map((shares) => ({ shares, action: 'delete' })));

    return merge(add$, update$, delete$).pipe(
      scan((acc: SnCollection<SnShare>, { shares, action }) => {
        switch (action) {
          case 'add':
          case 'update':
            acc.set(...shares);
            break;

          case 'delete': {
            acc.delete(...shares);
            break;
          }
        }

        return acc;
      }, new SnCollection()),
    );
  }

  private _getShareActions(): SnActionConfig<SnShare>[] {
    return [
      {
        label: t('common.config'),
        icon: 'settings',
        hidden: !this.canChange,
        click: this.configureShare.bind(this),
      },
      {
        label: t('shares.deleteShare'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-trash-alt',
        },
        color: 'warn',
        hidden: !this.canDelete,
        click: this.deleteShare.bind(this),
      },
    ];
  }
}
